/**
 * Reconnect LinkedIn page component - publicly accessible page for reconnecting a user's LinkedIn account
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Button,
    Typography,
    Avatar,
    Alert,
    CircularProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
  LinkedIn as LinkedInIcon,
} from '@mui/icons-material';
import { apiClient } from '@/api/client';
import { getLinkedInAuthUrl } from '@/api/client';

export const ReconnectLinkedIn: React.FC = () => {
  const { userId } = useParams<{ userId?: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);

  useEffect(() => {
    // Automatically fetch the auth URL when the component loads
    fetchAuthUrl();
  }, [userId]);

  const fetchAuthUrl = async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (userId) {
        // Reconnect existing user
        const response = await apiClient.get<{ output: { url: string; user_name: string } }>('/unipile/reconnect-url/', {
          params: { user_uuid: userId },
        });
        setAuthUrl(response.data.output.url);
        setUserName(response.data.output.user_name);
      } else {
        // Create new connection (like login page)
        const url = await getLinkedInAuthUrl();
        setAuthUrl(url);
        setUserName(null);
      }
    } catch (err: any) {
      console.error('Failed to get LinkedIn auth URL:', err);
      setError(
        err.response?.data?.error_messages?.[0] || 
        err.response?.data?.message || 
        'Failed to get LinkedIn authentication URL'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleLinkedInConnect = () => {
    if (authUrl) {
      window.location.href = authUrl;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        px: 2,
        py: 8,
      }}
    >
      <Box sx={{ maxWidth: 400, width: '100%' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            component="img"
            src="/logo/LogoAndCompanyBlack.png"
            alt="Scitodate Logo"
            onError={(e) => {
              // Fallback to icon if logo doesn't load
              console.error('Logo failed to load:', e);
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const fallback = document.getElementById('logo-fallback');
              if (fallback) fallback.style.display = 'flex';
            }}
            onLoad={() => {
              // Hide fallback when logo loads successfully
              const fallback = document.getElementById('logo-fallback');
              if (fallback) fallback.style.display = 'none';
            }}
            sx={{
              mx: 'auto',
              mb: 3,
              width: 'auto',
              height: 64,
              maxWidth: 200,
              objectFit: 'contain',
            }}
          />
          <Avatar
            id="logo-fallback"
            sx={{
              mx: 'auto',
              mb: 3,
              width: 64,
              height: 64,
              bgcolor: 'primary.main',
              boxShadow: 2,
              display: 'none',
            }}
          >
            <BusinessIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography variant="body2" color="text.secondary">
            {userId ? 'Reconnect LinkedIn Account' : 'Connect LinkedIn Account'}
          </Typography>
          {userName && (
            <Typography variant="body1" color="primary" sx={{ mt: 1, fontWeight: 500 }}>
              for {userName}
            </Typography>
          )}
        </Box>

        {/* LinkedIn Connection Card */}
        <Card sx={{ boxShadow: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Connect LinkedIn Account
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Connect your LinkedIn account to enable messaging features
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {isLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary">
                    {userId ? 'Preparing reconnection...' : 'Preparing connection...'}
                  </Typography>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<LinkedInIcon />}
                  onClick={handleLinkedInConnect}
                  disabled={!authUrl || !!error}
                  fullWidth
                  sx={{
                    py: 1.5,
                    fontWeight: 500,
                    fontSize: '1rem',
                    borderColor: '#0077b5',
                    color: '#0077b5',
                    '&:hover': {
                      borderColor: '#005885',
                      backgroundColor: 'rgba(0, 119, 181, 0.04)',
                    },
                    '&:disabled': {
                      borderColor: 'rgba(0, 0, 0, 0.26)',
                      color: 'rgba(0, 0, 0, 0.26)',
                    },
                  }}
                >
                  Connect LinkedIn Account
                </Button>
              )}

              {!isLoading && error && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={fetchAuthUrl}
                  sx={{ mt: 2 }}
                >
                  Retry
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};
